import React, { useState, useEffect } from "react";
import { Entity } from "./EntitySidebar";

interface AddEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (name: string, phone: string, timezone?: string) => void;
  editingEntity?: Entity | null;
}

export const AddEntityModal: React.FC<AddEntityModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  editingEntity,
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [timezone, setTimezone] = useState("Asia/Singapore");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!editingEntity;

  // Set initial values when editing
  useEffect(() => {
    if (isEditing && editingEntity) {
      setName(editingEntity.name);
      setPhone(editingEntity.phone);
      setTimezone(editingEntity.timezone || "Asia/Singapore");
    } else {
      setName("");
      setPhone("");
      setTimezone("Asia/Singapore");
    }
  }, [isEditing, editingEntity, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !phone.trim()) {
      alert("Please fill in both name and phone number");
      return;
    }

    setIsSubmitting(true);
    try {
      await onAdd(name.trim(), phone.trim(), timezone);
      if (!isEditing) {
        setName("");
        setPhone("");
        setTimezone("Asia/Singapore");
      }
    } catch (error) {
      console.error(isEditing ? 'Failed to update entity:' : 'Failed to add entity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName("");
    setPhone("");
    setTimezone("Asia/Singapore");
    onClose();
  };

  const modalTitle = isEditing ? "Edit Entity" : "Add Entity";
  const submitButtonText = isSubmitting ? (isEditing ? "Updating..." : "Creating...") : (isEditing ? "Update Entity" : "Add Entity");

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{modalTitle}</h2>
          <button className="modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="entity-name">Business Name:</label>
            <input
              id="entity-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., ABC Immigration Services"
              className="form-input"
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="entity-phone">Phone Number:</label>
            <input
              id="entity-phone"
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g., +65 9123 4567"
              className="form-input"
              disabled={isSubmitting}
              required
            />
            <small className="form-help">
              Enter the WhatsApp Business phone number with country code
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="entity-timezone">Timezone:</label>
            <select
              id="entity-timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="form-input"
              disabled={isSubmitting}
            >
              <option value="Asia/Singapore">Singapore (SGT, UTC+8)</option>
              <option value="America/New_York">New York (EST/EDT, UTC-5/-4)</option>
              <option value="America/Los_Angeles">Los Angeles (PST/PDT, UTC-8/-7)</option>
              <option value="America/Chicago">Chicago (CST/CDT, UTC-6/-5)</option>
              <option value="Europe/London">London (GMT/BST, UTC+0/+1)</option>
              <option value="Europe/Paris">Paris (CET/CEST, UTC+1/+2)</option>
              <option value="Asia/Tokyo">Tokyo (JST, UTC+9)</option>
              <option value="Asia/Hong_Kong">Hong Kong (HKT, UTC+8)</option>
              <option value="Asia/Shanghai">Shanghai (CST, UTC+8)</option>
              <option value="Asia/Dubai">Dubai (GST, UTC+4)</option>
              <option value="Asia/Kolkata">India (IST, UTC+5:30)</option>
              <option value="Australia/Sydney">Sydney (AEDT/AEST, UTC+11/+10)</option>
              <option value="Pacific/Auckland">Auckland (NZDT/NZST, UTC+13/+12)</option>
              <option value="UTC">UTC (UTC+0)</option>
            </select>
            <small className="form-help">
              Select the timezone for this business entity
            </small>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || !name.trim() || !phone.trim()}
            >
              {submitButtonText}
            </button>
          </div>
        </form>

        <div className="modal-info">
          <h4>💡 What happens next?</h4>
          <ul>
            <li>A new WhatsApp Business session will be created</li>
            <li>
              You'll get a unique QR code to scan with the WhatsApp Business
              mobile app
            </li>
            <li>Once connected, you can manage this session independently</li>
            <li>
              All chat data will be isolated between different business accounts
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
